/* Geometry for turning a signature into the fixed-size PNG (UIUX-4516).
   The approver page (layouts/onboard_approval.html.haml) inlines the same logic. */

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/* A channel below this counts as ink against the white pad; it matches the
   server's ContractTemplateSignatureInkChecker and keeps anti-aliased edges. */
const INK_THRESHOLD = 240;

/* Uniformly scales a source into a box and centres it, so the source keeps its
   aspect ratio and any spare space is left blank. `maxScale` stops a small
   source being blown up. */
export const fitWithin = (
  sourceWidth: number,
  sourceHeight: number,
  boxWidth: number,
  boxHeight: number,
  maxScale = Infinity
): Rect => {
  const ratio = Math.min(boxWidth / sourceWidth, boxHeight / sourceHeight, maxScale);
  const width = sourceWidth * ratio;
  const height = sourceHeight * ratio;
  return { x: (boxWidth - width) / 2, y: (boxHeight - height) / 2, width, height };
};

/* Bounding box of the ink in RGBA pixel data, grown by `padding` and clamped
   to the image, or null when nothing has been drawn. */
export const inkBounds = (
  data: Uint8ClampedArray,
  width: number,
  height: number,
  padding = 0
): Rect | null => {
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const offset = (y * width + x) * 4;
      /* Plain indexing: this runs over every pixel after each stroke, and
         data.at() is ~30x slower. The offsets are computed, not user input. */
      /* eslint-disable security/detect-object-injection */
      const opaque = data[offset + 3] > 0;
      const dark =
        data[offset] < INK_THRESHOLD ||
        data[offset + 1] < INK_THRESHOLD ||
        data[offset + 2] < INK_THRESHOLD;
      /* eslint-enable security/detect-object-injection */
      if (opaque && dark) {
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);
      }
    }
  }
  if (maxX < 0) return null;

  const left = Math.max(0, minX - padding);
  const top = Math.max(0, minY - padding);
  const right = Math.min(width, maxX + 1 + padding);
  const bottom = Math.min(height, maxY + 1 + padding);
  return { x: left, y: top, width: right - left, height: bottom - top };
};
